import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { format } from 'date-fns'
import { 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Database, 
  Cloud,
  TrendingUp,
  TrendingDown,
  Users,
  Settings,
  FileText,
  BarChart3
} from 'lucide-react'
import { useSmartleadDiscovery } from '@/hooks/useSmartleadDiscovery'
import { smartleadAPI } from '@/lib/smartlead-api'
import { DEMO_SYNC_STATUS, getDemoSyncStats } from '@/data/demoSyncStatus'

export function SmartleadDiscoveryDashboard() {
  // Demo mode check
  if (!smartleadAPI.isConfigured()) {
    console.log('🔑 Smartlead API not configured, showing demo discovery data...')
    
    const demoSyncStatus = DEMO_SYNC_STATUS
    const demoSyncStats = getDemoSyncStats()
    
    return (
      <div className="space-y-6 p-6">
        {/* Demo Mode Indicator */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Cloud className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Demo Mode - Smartlead Discovery Dashboard
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                Showing demo discovery/sync status with health checks and recent changes.
              </p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Smartlead Discovery Dashboard</h2>
            <p className="text-muted-foreground">
              Last synced: {format(new Date(demoSyncStatus.lastSyncTime), 'MMM d, yyyy h:mm a')}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              {demoSyncStatus.syncStatus.toUpperCase()}
            </Badge>
          </div>
        </div>

        {/* Demo Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Mailboxes</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{demoSyncStatus.totalMailboxes}</div>
              <p className="text-xs text-muted-foreground">
                Discovered mailboxes
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Campaigns</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{demoSyncStatus.totalCampaigns}</div>
              <p className="text-xs text-muted-foreground">
                Active campaigns
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Emails Synced</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{demoSyncStatus.emailsSyncedToday.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Today's sync volume
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <BarChart3 className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{demoSyncStatus.successRate}%</div>
              <p className="text-xs text-muted-foreground">
                Average sync success
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Health Checks */}
        <Card>
          <CardHeader>
            <CardTitle>System Health Checks</CardTitle>
            <CardDescription>
              Current status of all system components
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {demoSyncStatus.healthChecks.map((check) => (
                <div key={check.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                  <div className={`h-3 w-3 rounded-full ${
                    check.status === 'healthy' ? 'bg-green-500' :
                    check.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium">{check.name}</p>
                    <p className="text-sm text-muted-foreground">{check.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Changes */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Changes</CardTitle>
            <CardDescription>
              Latest updates and improvements to the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {demoSyncStatus.recentChanges.slice(0, 5).map((change) => (
                <div key={change.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                  <div className={`h-2 w-2 rounded-full ${
                    change.impact === 'high' ? 'bg-red-500' :
                    change.impact === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium">{change.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(change.timestamp), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  <Badge variant={
                    change.status === 'completed' ? 'default' :
                    change.status === 'in_progress' ? 'secondary' : 'outline'
                  }>
                    {change.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sync History */}
        <Card>
          <CardHeader>
            <CardTitle>Sync History</CardTitle>
            <CardDescription>
              Last 7 days of sync operations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {demoSyncStatus.syncHistory.slice(0, 7).map((record) => (
                <div key={record.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`h-3 w-3 rounded-full ${
                      record.status === 'success' ? 'bg-green-500' :
                      record.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <div>
                      <p className="font-medium">{record.details}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(record.date), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{record.duration}s</p>
                    <p className="text-xs text-muted-foreground">
                      {record.emailsSynced.toLocaleString()} emails
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
  const { toast } = useToast()
  const {
    discoveryResult,
    statusComparison,
    discrepancyReport,
    syncPlan,
    loading,
    error,
    lastDiscovery,
    runDiscovery,
    refreshStatusComparison,
    refreshDiscrepancyReport,
    refreshSyncPlan
  } = useSmartleadDiscovery()

  const [activeTab, setActiveTab] = useState('overview')
  const [syncing, setSyncing] = useState(false)

  const handleDailyMailboxSync = async () => {
    setSyncing(true)
    try {
      const { data, error } = await supabase.functions.invoke('daily-mailbox-status-sync')
      
      if (error) throw error

      toast({
        title: "Daily Mailbox Sync Started",
        description: "Daily mailbox status sync has been initiated. Check Slack for results.",
      })
    } catch (error) {
      console.error('Error starting daily mailbox sync:', error)
      toast({
        title: "Error",
        description: "Failed to start daily mailbox sync. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSyncing(false)
    }
  }

  if (loading && !discoveryResult) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Discovering mailboxes...</p>
          <p className="text-sm text-muted-foreground mt-2">
            Fetching all Smartlead mailboxes with pagination (this may take a moment)
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert className="m-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Discovery failed: {error}
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-2"
            onClick={runDiscovery}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  if (!discoveryResult) {
    return (
      <div className="p-4">
        <Button onClick={runDiscovery} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Start Discovery
        </Button>
      </div>
    )
  }

  const { summary, comparison, metadata } = discoveryResult

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Smartlead Discovery Dashboard</h2>
          <p className="text-muted-foreground">
            Compare database mailboxes with Smartlead status
            {lastDiscovery && (
              <span className="ml-2 text-sm">
                • Last updated: {new Date(lastDiscovery).toLocaleString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={runDiscovery}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Discovery
          </Button>
          <Button 
            variant="default" 
            onClick={handleDailyMailboxSync}
            disabled={loading || syncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            🔄 Daily Mailbox Sync
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Database Mailboxes</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalInDatabase}</div>
            <p className="text-xs text-muted-foreground">
              Total in database
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Smartlead Mailboxes</CardTitle>
            <Cloud className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalInSmartlead}</div>
            <p className="text-xs text-muted-foreground">
              Total in Smartlead
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successfully Matched</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summary.successfullyMatched}</div>
            <p className="text-xs text-muted-foreground">
              {summary.totalInDatabase > 0 
                ? `${Math.round((summary.successfullyMatched / summary.totalInDatabase) * 100)}% match rate`
                : 'No database mailboxes'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status Discrepancies</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{summary.statusDiscrepancies}</div>
            <p className="text-xs text-muted-foreground">
              Need attention
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="comparison">Status Comparison</TabsTrigger>
          <TabsTrigger value="discrepancies">Discrepancies</TabsTrigger>
          <TabsTrigger value="sync-plan">Sync Plan</TabsTrigger>
          <TabsTrigger value="metadata">Technical Details</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Database Only */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Database Only ({comparison.onlyInDatabase.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {comparison.onlyInDatabase.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span className="text-sm font-medium">{item.email}</span>
                      <Badge variant="secondary">DB Only</Badge>
                    </div>
                  ))}
                  {comparison.onlyInDatabase.length > 5 && (
                    <p className="text-sm text-muted-foreground">
                      +{comparison.onlyInDatabase.length - 5} more...
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Smartlead Only */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="h-5 w-5" />
                  Smartlead Only ({comparison.onlyInSmartlead.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {comparison.onlyInSmartlead.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span className="text-sm font-medium">{item.email}</span>
                      <Badge variant="outline">Smartlead Only</Badge>
                    </div>
                  ))}
                  {comparison.onlyInSmartlead.length > 5 && (
                    <p className="text-sm text-muted-foreground">
                      +{comparison.onlyInSmartlead.length - 5} more...
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Mismatches */}
          {comparison.statusMismatches.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  Status Mismatches ({comparison.statusMismatches.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {comparison.statusMismatches.slice(0, 10).map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                      <span className="text-sm font-medium">{item.email}</span>
                      <div className="flex gap-2">
                        <Badge variant="outline">
                          DB: {item.databaseData?.status || 'Unknown'}
                        </Badge>
                        <Badge variant="outline">
                          SL: {item.smartleadData?.status || 'Unknown'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {comparison.statusMismatches.length > 10 && (
                    <p className="text-sm text-muted-foreground">
                      +{comparison.statusMismatches.length - 10} more...
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="comparison" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Status Comparison</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={refreshStatusComparison}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          <div className="space-y-2">
            {statusComparison.slice(0, 20).map((comparison, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{comparison.email}</span>
                  <div className="flex gap-2">
                    <Badge variant={comparison.statusMatch ? "default" : "destructive"}>
                      DB: {comparison.databaseStatus}
                    </Badge>
                    <Badge variant={comparison.statusMatch ? "default" : "destructive"}>
                      SL: {comparison.smartleadStatus}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {comparison.overallMatch ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="discrepancies" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Discrepancy Report</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={refreshDiscrepancyReport}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {discrepancyReport && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Critical</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {discrepancyReport.criticalDiscrepancies}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">High</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600">
                    {discrepancyReport.highDiscrepancies}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Medium</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">
                    {discrepancyReport.mediumDiscrepancies}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Low</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {discrepancyReport.lowDiscrepancies}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sync-plan" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Sync Plan</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={refreshSyncPlan}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {syncPlan && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Create</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {syncPlan.actions.create.length}
                    </div>
                    <p className="text-xs text-muted-foreground">New mailboxes</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Update</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {syncPlan.actions.update.length}
                    </div>
                    <p className="text-xs text-muted-foreground">Status changes</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Deactivate</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {syncPlan.actions.deactivate.length}
                    </div>
                    <p className="text-xs text-muted-foreground">Remove from DB</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Risk Level</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge 
                      variant={
                        syncPlan.riskLevel === 'high' ? 'destructive' : 
                        syncPlan.riskLevel === 'medium' ? 'secondary' : 'default'
                      }
                    >
                      {syncPlan.riskLevel.toUpperCase()}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Est. {Math.round(syncPlan.estimatedTime / 1000)}s
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Recommendations */}
              {syncPlan.recommendations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {syncPlan.recommendations.map((rec, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                          <div>
                            <span className="font-medium">{rec.email}</span>
                            <p className="text-sm text-muted-foreground">{rec.reason}</p>
                          </div>
                          <Badge variant={
                            rec.priority === 'high' ? 'destructive' : 
                            rec.priority === 'medium' ? 'secondary' : 'default'
                          }>
                            {rec.priority}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="metadata" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Technical Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Discovery Started:</span>
                  <span>{new Date(metadata.discoveryStarted).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Discovery Completed:</span>
                  <span>{new Date(metadata.discoveryCompleted).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Processing Time:</span>
                  <span>{metadata.processingTimeMs}ms</span>
                </div>
                <div className="flex justify-between">
                  <span>API Calls Made:</span>
                  <span>{metadata.apiCallsMade}</span>
                </div>
                {metadata.errors.length > 0 && (
                  <div>
                    <span className="text-red-600">Errors:</span>
                    <ul className="list-disc list-inside ml-4">
                      {metadata.errors.map((error, index) => (
                        <li key={index} className="text-red-600">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
